/* ═══════════════════════ ENGLISH — SOURCE LANGUAGE ═══════════════════════
   The master key list. Every other locale file must define exactly these keys;
   `App.test.js` fails the build otherwise, which is what stops a language
   shipping half-translated.

   `{max}` and `{email}` are filled by i18n.mjs from MAX_PARTICIPANTS and
   SUPPORT_EMAIL, so the participant cap in the translated marketing copy and
   the cap the Firebase rules actually enforce cannot drift apart.

   English page *content* is not here — it stays in routeMeta.mjs, which is the
   thing every translation was made from. This file is UI chrome only.
═══════════════════════════════════════════════════════════════════════════ */
export const ui = {
  /* ── Language switcher ── */
  "lang.label": "Language",
  "lang.aria": "Choose a language",
  "lang.current": "Choose a language. Current: {language}",

  /* ── Global chrome ── */
  "skip.main": "Skip to main content",
  "nav.aria": "Main navigation",
  "nav.brandHome": "Point Poker, go to home",
  "nav.sections": "Marketing sections",
  "nav.pricing": "Pricing",
  "nav.support": "Support",
  "nav.trust": "Trust",
  "nav.faq": "FAQ",
  "nav.toPricing": "Go to pricing",
  "nav.toSupport": "Go to support",
  "nav.toTrust": "Go to trust and reliability",
  "nav.toFaq": "Go to frequently asked questions",
  "nav.history": "History",
  "nav.viewHistory": "View sprint history",
  "nav.account": "Signed-in account",
  "nav.plan": "Free",
  "nav.dashboard": "Usage dashboard",
  "nav.signOut": "Sign out",
  "nav.signIn": "Sign in",
  "nav.startFree": "Start a free room",
  "nav.startShort": "Start",

  /* ── Footer ── */
  "footer.aria": "Site footer",
  "footer.freeText":
    "Every feature, every team, no card. Up to {max} people per room, unlimited rounds, unlimited stories, no ads.",
  "footer.laterChip": "Later",
  "footer.laterText":
    "Paid add-ons may arrive once the tool has a real user base. Everything listed here stays free.",
  "footer.descSignedIn":
    "Your workspace is live. Reuse either Team Room, share the invite link, and keep sprint history attached to your account.",
  "footer.descGuest":
    "Free, real-time planning poker for agile and Scrum teams. No sign-up required. Works in any browser.",
  "footer.descGuest2":
    "Built for Product Owners, Scrum Masters, and distributed teams who need fast, structured story-point consensus.",
  "footer.legal": "Legal",
  "footer.terms": "Terms of Service",
  "footer.privacy": "Privacy Policy",
  "footer.cookies": "Cookie Settings",
  "footer.gdpr": "Data & GDPR",
  "footer.product": "Product",
  "footer.account": "Account",
  "footer.workspaceActive": "Workspace active · Free",
  "footer.features": "Features",
  "footer.support": "Support",
  "footer.emailSupport": "Email support",
  "footer.home": "Free Planning Poker",
  "footer.about": "About Point Poker",
  "footer.trustRel": "Trust & reliability",
  "footer.pricingPlans": "Pricing & plans",
  "footer.ppOnline": "Planning poker online",
  "footer.supportContact": "Support & contact",
  "footer.guides": "Guides",
  "footer.guideWhatIs": "What is planning poker?",
  "footer.guidePointing": "Pointing poker",
  "footer.guideFib": "Fibonacci story points",
  "footer.guideHours": "Story points to hours",
  "footer.guideEstimation": "Story point estimation",
  "footer.guideJira": "Planning poker with Jira",
  "footer.guideScrum": "Scrum poker",
  "footer.guideRemote": "Remote sprint planning",
  "footer.guideAgile": "Agile estimation tool",
  "footer.builtBy": "Built and run by",
  "footer.copyright": "© {year} Point Poker. All rights reserved. Registered in England & Wales.",
  "footer.legalNote1": "Point Poker is provided “as-is” without warranty of any kind. Use is subject to our",
  "footer.legalNote2":
    ". Firebase and Vercel are third-party services and are not affiliated with Point Poker.",
  /* English-only pages carry a note in the reader's language saying so, rather
     than silently handing them a page they cannot read. */
  "footer.englishOnly": "Legal pages are available in English only.",

  /* ── Cookie / storage notice ── */
  "cookie.aria": "Storage notice",
  "cookie.title": "Essential browser storage only",
  "cookie.body":
    "Firebase keeps your session; your display name and this notice are remembered locally. No advertising, tracking, or third-party analytics cookies, nothing to opt out of.",
  "cookie.privacy": "Privacy",
  "cookie.terms": "Terms",
  "cookie.gotIt": "Got it",

  /* ── Marketing page chrome (ContentPage / MarketingPageShell) ── */
  "page.back": "← Back to home",
  "page.startFree": "Start a free room",
  "page.seeFeatures": "See all features",
  "page.viewPricing": "View pricing",
  "page.howItWorks": "How it works",
  "page.faqTitle": "Frequently asked questions",
  "page.faqIntro": "The questions people actually type into a search box, answered straight.",
  "page.relatedTitle": "Related pages",
  "page.relatedIntro": "Where to go next, depending on whether you want the theory or the room.",

  /* ── Join screen: hero ── */
  "join.welcomeBack": "Welcome back",
  "join.welcomeBackNamed": "Welcome back, {name}",
  "join.title": "Free Planning Poker for Agile Teams",
  "join.subSignedIn": "Open a fixed Team Room for recurring planning, or set up a one-off session.",
  "join.sub":
    "Deal a room, share the link, everyone reveals at once. Every feature is free, and you do not need an account to play.",
  "join.trustAria": "What you get",
  "join.trust1": "Free for everyone",
  "join.trust2": "No sign-up to play",
  "join.trust3": "Up to {max} at the table",
  "join.trust4": "No ads",

  /* ── Join screen: workspace panel ── */
  "join.yourTeamRooms": "Your Team Rooms",
  "join.yourTeamRoomsSub":
    "Two fixed URLs tied to your account. Share them once and reuse them every sprint.",
  "join.room1": "Room 1",
  "join.room2": "Room 2",
  "join.openRoom": "Open {room} →",
  "join.linkCopied": "Link copied",
  "join.copyLink": "Copy link",
  "join.renameBoth": "Rename both rooms",
  "join.sharedRoomName": "Shared room name",
  "join.renamePlaceholder": "e.g. Product Planning",
  "join.saving": "Saving…",
  "join.save": "Save",
  "join.renameSaved": "Saved. Share the new links with your team.",

  /* ── Join screen: form ── */
  "join.tabsAria": "What you want to do",
  "join.tabCreate": "Create",
  "join.tabJoin": "Join",
  "join.tabTeam": "Team",
  "join.yourName": "Your name",
  "join.namePlaceholder": "e.g. Alex Johnson",
  "join.nameHint":
    "The name the rest of the table sees. Changing it here does not change your account.",
  "join.roomCode": "Room code",
  "join.roomCodePlaceholder": "e.g. A1B2C",
  "join.teamName": "Team name",
  "join.teamNamePlaceholder": "e.g. Product Team",
  "join.teamRoomsFree": "Team Rooms are free",
  "join.teamRoomsFreeBody":
    "They need a free account so that nobody else can claim your room URL, which takes about thirty seconds.",
  "join.createFreeAccount": "Create a free account →",
  "join.teamReady": "This team's room is ready. Add your name, choose your role, and join the live session.",
  "join.teamReadyGuest":
    "This team's room is ready. Add your name, choose your role, and join the live session, no account needed.",
  "join.yourRole": "Your role",
  "join.roleVoter": "Participant",
  "join.roleVoterDesc": "Votes on each story",
  "join.roleObserver": "Facilitator",
  "join.roleObserverDesc": "Runs the session and does not vote",
  "join.cardDeck": "Card deck",
  "join.estimating": "Estimating",
  "join.permanence": "Both are fixed for this room once created.",
  "join.createRoom": "Create Room →",
  "join.joinRoom": "Join Room →",
  "join.joinTeamRoom": "Join Team Room →",
  "join.needAccount": "Create a free account for 2 Team Rooms →",
  "join.noteCreate": "Free · Up to {max} at the table · Live in ten seconds",
  "join.noteJoin": "Got a link instead? Open it and you'll join straight away.",
  "join.noteTeam": "Two fixed Team Rooms per account. Same links, every sprint, free.",

  /* ── Join screen: validation ── */
  "join.errName": "Please enter your name.",
  "join.errRealName": "Please enter your real name before joining.",
  "join.errCode": "Please enter a room code",
  "join.errTeam": "Please enter your team name",
  "join.errRenameEmpty": "Choose a name for your Team Rooms.",
  "join.errRenameSave": "Could not save your Team Room names right now. Try again.",
  "join.errCopy": "Your browser blocked the copy. Select the link and copy it manually.",

  /* ── Home marketing block (rendered inside JoinScreen) ── */
  "home.aria": "About Point Poker",
  "home.h2": "Free Online Planning Poker for Sprint Planning, Scrum Poker, and Remote Estimation",
  "home.h2sub":
    "Point Poker gives agile teams a fast, low-friction way to run planning poker online. Create a room, share one link in Slack, Teams, or Zoom, and let everyone vote at the same time. No install, no training, no ads, and no account needed to play.",
  "home.freeLead": "Everything is free: every feature, for every team.",
  "home.freeBody":
    "Other planning poker tools cap your free sessions at a handful of votes, seven participants, or hide the timer and averages behind a paid plan. Here you get {max} people per room, unlimited voting rounds, unlimited stories, all three card decks, the timer, full analytics, and export, for $0. We are focused on being genuinely useful to as many teams as possible first. If paid add-ons ever arrive, everything listed on this page stays free.",
  "home.revealTitle": "Why simultaneous reveal matters",
  "home.revealBody":
    "Every team member votes independently before estimates are shown. Cards reveal all at once, which reduces anchoring bias and leads to better story-point conversations. You get clearer estimates, faster discussions, and fewer meetings dominated by the loudest voice.",
  "home.howTitle": "How it works",
  "home.howSteps": [
    "Create a room or join one from a shared link",
    "Add the item you are estimating, a user story or a specific task within one",
    "Vote with Fibonacci, T-Shirt sizing, or Powers of 2",
    "Reveal cards together and discuss only when estimates differ",
    "Let the facilitator record the final agreed estimate or run another vote",
    "Move straight to the next item without resetting the room",
  ],
  "home.plansAria": "Pricing overview",
  "home.plansTitle": "What it costs: nothing",
  "home.plansSub":
    "One product, free for every team. No tiers, no trial clock, no card.",
  "home.planEveryone": "Everyone",
  "home.planFreeList": [
    "Up to {max} participants including facilitators",
    "Unlimited rounds and unlimited stories per session",
    "All card decks, story or task queue, countdown timer",
    "Facilitator mode, live analytics, clipboard and CSV export",
    "Two fixed Team Rooms and sprint history with a free account",
  ],
  "home.planComparedWith": "Compared with",
  "home.planOtherList": [
    "Common free caps elsewhere: 7 participants, or 9 votes per game",
    "Timers and averages often sit behind a paid tier",
    "Some free tools are ad-supported",
    "Per-facilitator pricing adds up fast for one ceremony a sprint",
  ],
  "home.pricingCta": "Read the full pricing promise",
  "home.diffTitle": "What makes this planning poker tool different",
  "home.diffList": [
    "Zero setup, every time: create a room and share the link in under 10 seconds, no account needed",
    "Simultaneous vote reveal: prevents anchoring bias so every estimate is honest and independent",
    "Three card decks: Fibonacci (1 to 34), T-shirt sizing (XS to XXL), or Powers of 2, whichever matches how your team thinks",
    "Story or task estimation: choose whether you are sizing user stories as a whole or individual tasks within them; the queue, banners, and analytics all adapt to your choice",
    "Item queue: load your full sprint backlog or task list and work through it in order, one item at a time",
    "Team Alignment analytics: facilitators see live consensus rate, total story points, estimate distribution, and re-vote patterns",
    "Estimation Spree: a live streak counter celebrates when the team aligns consistently, reinforcing good backlog clarity",
    "Built-in countdown timer: keep each estimation round time-boxed and the whole session on track",
    "Session summary: copy every estimate to the clipboard or download a CSV for Jira, Linear, Azure DevOps, or a spreadsheet",
    "Facilitator mode: join without a vote card and manage reveal, re-votes, participant moderation, and session flow from the analytics view",
    "Team Rooms: two fixed URLs your teams reuse every sprint, no fresh setup each time. Free with a free account",
    "Keyboard shortcuts: press 1–9 to vote, R to reveal, N for the next item; the whole ceremony without touching the mouse",
    "No ads and no tracking cookies: nothing to block, nothing sold, nothing following your team around",
  ],
  "home.exploreLead": "Explore the dedicated pages for",
  "home.exploreTail": "to learn how the workflow fits your team.",
  "home.linkFeatures": "features",
  "home.linkOnline": "planning poker online",
  "home.linkScrum": "Scrum poker",
  "home.linkEstimation": "story point estimation",
  "home.linkWhatIs": "what planning poker is",
  "home.linkFib": "Fibonacci story points",
  "home.linkAgile": "agile estimation tools",
  "home.linkPointing": "pointing poker",
  "home.linkTrust": "trust and reliability",
  "home.faqTitle": "Frequently asked questions",

  /* ── Room action bar ── */
  "action.aria": "Session controls",
  "action.cardsUp": "Cards are up",
  "action.waiting": "Waiting for the table",
  "action.inProgress": "Round in progress",
  "action.voted": "{done} of {total} voted",
  "action.progressAria": "{done} of {total} voters have played a card",
  "action.inviteCopied": "Invite link copied",
  "action.copyInvite": "Copy the invite link",
  "action.reveal": "Reveal everyone's cards",
  "action.hintSplit": "Votes are split. Agree a number below, then record it.",
  "action.hintDone": "Round complete. The round's actions are under the estimate below.",
  "action.hintEmpty": "Send it to your team. They join in one tap, no account needed.",
  "action.hintFirstOne": "Waiting for the first card from your voter.",
  "action.hintFirstMany": "Waiting for the first card from your {count} voters.",
  "action.hintAllIn": "Everyone is in. Reveal when you are ready.",
  "action.hintEarly": "Reveal early if the room has stopped thinking.",
  "action.timeUp": "Time is up",
  "action.revealTimeUp": "Time is up — reveal everyone's cards",
  "action.hintTimeUp": "The countdown ended. Reveal when the room is ready, or start another timer.",
  "action.hintTimeUpNone": "The countdown ended with no cards played. Start another timer, or talk it through first.",

  /* ── Game screen ── */
  "game.leaveAria": "Leave room and return to home",
  "game.returnHome": "Return to home",
  "game.round": "Round {n}",
  "game.inviteAria": "Invite team",
  "game.copyInviteAria": "Copy invite link to clipboard",
  "game.copyInvite": "Copy invite link",
  "game.inviteCopied": "Invite link copied",
  "game.linkPersistent": "Dedicated Team Room link",
  "game.linkTemporary": "Temporary room link",
  "game.linkPersistentHint": "Share once and reuse it every sprint.",
  "game.linkTemporaryHint": "Share it while this session is active.",
  "game.teamRoomReady": "Team Room ready",
  "game.teamRoomBody": "Share the link once. It stays the same every sprint.",
  "game.sessionEndsBody": "This room closes in about 10 minutes. Finish the story you are on.",
  "game.nobodyYetBody": "Share the invite link and the table fills up as people arrive.",
  "game.sizeBreakdown": "T-Shirt size breakdown",
  "game.dismiss": "Dismiss",
  "game.sessionEnds": "Session ends soon",
  "game.nowEstimating": "Now estimating · {label} {n} of {total}",
  "game.timerTitle": "Estimation timer",
  "game.timerOptional": "optional",
  "game.leave": "← Leave",
  "game.roomAria": "Planning poker room {code}, round {round}",
  "game.timer30": "30 seconds",
  "game.timer45": "45 seconds",
  "game.timer60": "1 minute",
  "game.startCountdown": "Start {n} countdown",
  "game.oneMin": "1 min",
  "game.timerHint": "The team can vote without this. Use it if you want to time-box the round.",
  "game.stopTimer": "Stop timer",
  "theme.dark": "Dark",
  "theme.light": "Light",
  "theme.suffix": " theme",
  "game.countdownLength": "Countdown length",
  "game.timeUp": "Time's almost up!",
  "game.wrappingUp": "Wrapping up…",
  "game.estimating": "Estimating…",
  "game.timerEndsHint": "You reveal the cards when it ends",
  "game.timerExpiredTitle": "Time is up",
  "game.timerExpiredBody": "The cards are still face down. Reveal them with the button above when the room is ready, or start another countdown to give the team more time.",
  "game.timeUpVoter": "Time is up. The facilitator reveals the cards — you can still play one until they do.",
  "game.pickNow": "Pick a card, NOW!",
  "game.lastSeconds": "Last few seconds!",
  "game.pickYourCard": "Pick your card!",
  "game.facilitatorReveals": "Facilitator reveals the cards",
  "game.splitHint": "Votes are split, discuss briefly, then confirm the agreed estimate.",
  "game.doneHint":
    "Round complete, record it from the row under the estimate when you are ready to continue.",
  "game.playWhenReady":
    "Play your card whenever you are ready. The timer is optional, and the cards flip once everyone has voted.",
  "game.yourEstimate": "Your Estimate",
  "game.voteAria": "Vote {value}",
  /* Split around the <kbd> keys rather than interpolated into a sentence: a
     translation has to be free to put the keys somewhere else in the clause. */
  "game.tipNumericLead": "Tip: type the value you want.",
  "game.tipNumericTail": ", or",
  "game.tipNumericEnd": "for the wild card.",
  "game.tipOrderLead": "Tip: press",
  "game.tipOrderTail": "for the sizes in order, or",
  "game.consensusReached": "Consensus reached. The facilitator records it and moves on.",
  "game.cardsUpDiscuss": "Cards are up. Discuss the spread while the facilitator confirms the estimate.",
  "game.youPicked": "You picked {value}. Waiting for the rest of the table.",
  "game.pickToVote": "Pick a card to cast your vote.",
  "game.resultsAria": "Vote results",
  "game.perfectConsensus": "Perfect Consensus!",
  "game.allPicked": "All {count} voters picked",
  "game.everyonePicked": "Everyone who voted picked",
  "game.agreedEstimate": "Agreed estimate",
  "game.averageVote": "Average vote",
  "game.allSameHint": "Everyone who voted picked the same card.",
  "game.allUnknownHint":
    "Everyone played ?. Nobody has enough to size this yet, clarify the item, then re-vote.",
  "game.spreadHint":
    "Use the range below to guide the discussion. The facilitator records the final agreed estimate next.",
  "game.min": "Min",
  "game.max": "Max",
  "game.spreadSub": "Spread: {n} point — discuss, then record one final deck value",
  "game.spreadSubPlural": "Spread: {n} points — discuss, then record one final deck value",
  "game.median": "Median",
  "game.average": "Average",
  "game.spread": "Spread",
  "game.spreadPoints": "{n} point",
  "game.spreadPointsPlural": "{n} points",
  "game.differentVotes": "Different votes",
  "game.votesShown": "Votes shown",
  "game.nobodyVoted": "Nobody has voted yet",
  "game.whoPickedWhat": "Who Picked What",
  "game.highest": "Highest",
  "game.lowest": "Lowest",
  "game.agreed": "Agreed",
  "game.facilitatorAria": "Facilitator choose final estimate",
  "game.facilitatorDecision": "Facilitator decision",
  "game.nobodyCouldSize": "Nobody could size this one",
  "game.chooseAgreed": "Choose the agreed estimate for this item",
  "game.allUnknownBody":
    "Every voter played ?. That is a signal the item needs clearer acceptance criteria, not a number. Clarify it and re-vote, or record a placeholder and come back to it.",
  "game.mixedBody":
    "The votes are mixed. Select the estimate your team agrees to record, then move straight to the next item. The summary above is only for discussion.",
  "game.chooseFinalAria": "Choose final estimate",
  "game.recordValueAria": "Record {value} as the agreed estimate",
  "game.pickAgreed": "Pick the agreed estimate",
  "game.recordNextItem": "Record {value} & next item",
  "game.recordNextRound": "Record {value} & next round",
  "game.roundActionsAria": "Round actions",
  "game.addItem": "Add an item",
  "game.addTask": "Add a task",
  "game.addHint": "Paste a whole list, one {unit} per line, and every line gets queued at once.",
  "game.removeFromQueue": "Remove from queue",
  "game.removeNamed": "Remove {name} from the queue",
  "game.atTheTable": "At the Table",
  "game.nobodyYet": "Nobody here yet",
  "game.votedLabel": "Voted",
  "game.notVoted": "Hasn't voted yet",
  "game.remove": "Remove",
  "game.removeFromRoom": "Remove {name} from the room",
  "game.facilitatorNoVote": "Facilitator · No vote",
  "game.sprintAnalytics": "Sprint Analytics",
  "game.nothingRecorded": "Nothing recorded yet",
  "game.recordFirst": "Record your first story to start tracking alignment.",
  "game.mostUsedSize": "Most used size",
  "game.sprintScope": "Sprint scope",
  "game.sizeMix": "Size mix",
  "game.avgPer": "Avg / {unit}",
  "game.afterFirst": "After the first estimate",
  "game.teamAlignment": "Team Alignment",
  "game.teamAlignmentAria": "Team alignment {n} per cent",
  "game.alignExcellent": "Excellent",
  "game.alignLow": "Low consensus",
  "game.colEstimate": "Estimate",
  "game.colRemove": "Remove",
  "game.colItem": "Item",
  "game.deleteEstimateAria": "Delete the {estimate} estimate for {name}",
  "game.noneSized": "No {plural} sized yet",
  "game.addNamesHint": "Add {singular} names to the queue to track estimates here.",
  "game.estimatesAppear": "Estimates appear here after the first recorded round.",
  "game.spree": "Estimation spree!",
  "game.spreeN": "{n}-round spree!",
  "game.spree1": "First consensus, everyone on the same page",
  "game.spree2": "Two in a row, team understands the work",
  "game.spree3": "Team is locked in, great backlog clarity",
  "game.spree4": "Unstoppable. The team is perfectly aligned.",
  "game.alignGood": "Good",
  "game.alignFair": "Fair",
  "game.alignSub": "{done} of {total} {unit} agreed first vote",
  "game.alignRevote": " · {n} re-vote",
  "game.alignRevotePlural": " · {n} re-votes",
  "game.deckTshirtSizes": "T-Shirt sizes",
  "game.unitPoints": " sp",
  "game.pluralSized": "{plural} sized",
  "game.colIndex": "#",
  "game.notEstimated": "not estimated",
  "game.reportMeta": "Room {code} · {date} · {sized} of {total} {plural} sized",
  "game.reportPointsTotal": " · {n} points total",
  "game.summaryCounts": "{plural}: {total} · estimated: {sized}",
  "game.queueAdded": "{n} {plural} added to the queue.",
  "game.allEstimated": "All {n} {plural} estimated.",
  "game.allEstimatedTail": " — all {plural} estimated!",
  "game.doneBadge": "done",
  "game.analyticsEmpty":
    "Consensus rate, spread, and {singular} totals appear here after the first recorded round.",
  "game.alignNote": "% of {plural} where all voters agreed on the first vote",
  "game.sprintSummary": "Sprint Summary",
  "game.deleteConfirm": "Delete this {unit}?",
  "game.confirmDelete": "Confirm delete",
  "game.deleteBody": "It comes off the sized list and out of the sprint totals. Estimating it again means running another round.",
  "game.cancel": "Cancel",
  "game.confirmReset":
    "Start a new sprint? This clears every vote, estimate and round for everyone in the room. Your story queue is kept.",
  "game.confirmEnd":
    "End the session? This disconnects everyone and permanently deletes all session data.",
  "game.summaryTitle": "Sprint estimation summary",
  "game.summaryTitleTeam": "{team} — estimates",
  "game.summaryTotal": "Total points: {n}",
  "game.summaryFooter": "Estimated with Point Poker — {domain}",
  "game.reportRoom": "Room {code} · {date}",

  /* ── Toasts ── */
  "toast.inviteCopied": "Invite link copied.",
  "toast.copyBlockedLink": "Copy blocked by the browser, select the link above and copy it.",
  "toast.summaryCopied": "Summary copied to your clipboard.",
  "toast.copyBlockedCsv": "Copy blocked by the browser, use the CSV download instead.",
  "toast.roomFull":
    "This room is full. {max} people are already in, including the facilitator. Ask the host to start a second room.",
  "toast.teamRoomFull": "This Team Room is full. {max} people are already in.",
  "toast.accountCreated": "Account created, check your email to verify the address.",
  "toast.accountCreatedNoMail": "Account created. Verification email could not be sent yet.",
  "toast.signedIn": "Signed in.",

  /* ── Login modal ── */
  "login.yourAccount": "Your account",
  "login.createTitle": "Create your free account",
  "login.resetTitle": "Reset your password",
  "login.signInTitle": "Sign in",
  "login.subAccount":
    "Everything on Point Poker is free. This account holds your two Team Rooms and your sprint history.",
  "login.subTeamRoom":
    "Team Rooms are free. The account exists so the room URL is yours and no other team can land in it.",
  "login.subRegister":
    "Free, no card, about thirty seconds. You get two permanent Team Room links and your sprint history.",
  "login.subReset": "Enter your account email and we’ll send a password reset link.",
  "login.subSignIn": "Welcome back. Your Team Rooms and sprint history are waiting.",
  "login.tabsAria": "What you want to do",
  "login.tabSignIn": "Sign in",
  "login.tabRegister": "Create account",
  "login.tabReset": "Reset password",
  "login.fullName": "Full name",
  "login.namePlaceholder": "Alex Johnson",
  "login.accountEmail": "Account email",
  "login.email": "Email",
  "login.emailPlaceholder": "you@company.com",
  "login.password": "Password",
  "login.passwordMin": "Minimum 6 characters",
  "login.passwordYour": "Your password",
  "login.signingIn": "Signing in…",
  "login.creating": "Creating account…",
  "login.createClaim": "Create account & claim my Team Rooms",
  "login.createFree": "Create free account",
  "login.continueTeamRooms": "Continue to my Team Rooms",
  "login.continueWorkspace": "Continue to workspace",
  "login.sendingVerification": "Sending verification…",
  "login.resendVerification": "Resend verification email",
  "login.sendingReset": "Sending reset…",
  "login.sendReset": "Send reset link",
  "login.sendResetLong": "Send password reset email",
  "login.signedInAs": "Signed in as",
  "login.currentAccount": "Current account",
  "login.signedIn": "Signed in",
  "login.everythingUnlocked": "Free, everything unlocked",
  "login.notVerified": "Your email address is not verified yet",
  "login.whatAccountGives": "What this account gives you",
  "login.whatAccountAdds": "What an account adds",
  "login.accountActive":
    "Two permanent Team Room links and your sprint history, both already active on this account.",
  "login.accountAdds":
    "Two permanent Team Room links that never change, plus sprint history so you can see velocity and alignment over time. Both free.",
  "login.elseFree": "Everything else is already free without an account.",
  "login.elseFreeBody":
    "Rooms, all card decks, {max} participants, the queue, timer, analytics, and export work for anyone with the link.",
  "login.notWorking": "Something not working?",
  "login.emailSupport": "Email support ↗",

  "wtp.thanks": "Thank you, that genuinely shapes what gets built next.",
  "wtp.eyebrow": "One question, then never again",
  "wtp.dismiss": "Dismiss this question",
  "wtp.question":
    "Point Poker is free and staying free. If it were paid, what would this be worth to your team?",
  "wtp.note": "Anonymous. No email, no follow-up, no change to your access.",
  "wtp.zero": "Nothing, free is the reason we use it",
  "wtp.5": "Up to $5 a month for the team",
  "wtp.15": "$6–15 a month for the team",
  "wtp.30": "More than $15 a month for the team",

  /* ── Auth errors ── */
  "auth.emailInUse": "That email address already has an account.",
  "auth.invalidEmail": "Enter a valid email address.",
  "auth.badCredentials": "Email or password not recognised.",
  "auth.weakPassword": "Password must be at least 6 characters.",
  "auth.tooMany": "Too many attempts. Wait a moment and try again.",
  "auth.generic": "Could not complete that request. Try again.",
  "auth.verifyTooMany": "Too many verification attempts. Wait a moment and try again.",
  "auth.verifyDomain":
    "We could not send the verification email from this domain right now. Try again shortly or contact support.",
  "auth.verifyGeneric": "We could not send the verification email right now. Try again.",
  "auth.noEmailForVerify": "This account does not have an email address available for verification.",
  "auth.enterEmailPassword": "Enter your email and password.",
  "auth.enterName": "Enter your name so teammates can recognise you.",
  "auth.enterEmailForReset": "Enter your email and we'll send a reset link.",
  "auth.noResetAvailable": "This account does not have a password reset email available.",
  "auth.signedIn": "Signed in.",
  "auth.accountCreatedVerify": "Account created. Check {email} to verify your address.",
  "auth.verifyResent": "Verification email resent to {email}.",
  "auth.yourEmail": "your email",
  "auth.yourInbox": "your inbox",
  "auth.accountCreatedNoMail":
    "Account created, but we could not send the verification email yet. Use resend below.",
  "auth.resetSent": "Password reset email sent.",

  /* ── Sprint history ── */
  "history.title": "Sprint history",
  "history.none": "No sprint sessions recorded yet",
  "history.count": "{n} session recorded",
  "history.countPlural": "{n} sessions recorded",
  "history.emptyTitle": "Your sprint archive is ready",
  "history.emptyBody":
    "Finish a session while signed in and it will appear here automatically. Sprint history is saved when you end a session, or when a room auto-expires after five hours.",
  "history.avgVelocity": "Avg velocity",
  "history.ptsPerSprint": "pts / sprint",
  "history.appearsOnce": "Appears once a sprint records points",
  "history.bestSprint": "Best sprint",
  "history.storyPts": "story pts",
  "history.teamAlignment": "Team alignment",
  "history.avgConsensus": "avg consensus",
  "history.velocityTrend": "Velocity trend",
  "history.trendMeta": "recent half vs earlier half",
  "history.needsTwo": "Needs two or more sprints",
  "history.caption": "Most recent session first.",
  "history.colSprint": "Sprint",
  "history.colEnded": "Ended",
  "history.colPoints": "Points",
  "history.colStories": "Stories",
  "history.colConsensus": "Consensus",
  "history.colDuration": "Duration",
  "history.sprintN": "Sprint {n}",

  /* ── Decks and estimation modes ── */
  "deck.fibonacci": "Fibonacci",
  "deck.tshirt": "T-Shirt",
  "deck.powers": "Powers of 2",
  "deck.fibonacci.desc": "1, 2, 3, 5, 8, 13, 21, 34, ?",
  "deck.tshirt.desc": "XS, S, M, L, XL, XXL",
  "deck.powers.desc": "1, 2, 4, 8, 16, 32, ?",
  "mode.stories.label": "User Stories",
  "mode.stories.desc": "Estimate each story as a whole",
  "mode.stories.singular": "story",
  "mode.stories.plural": "stories",
  "mode.stories.queueTitle": "Story Queue",
  "mode.stories.progressLabel": "Story",
  "mode.stories.bannerLabel": "Estimating",
  "mode.stories.allDoneText": "stories estimated",
  "mode.stories.backlogLabel": "Sprint backlog",
  "mode.stories.toastDone": "Story estimated. Vote on the next story.",
  "mode.stories.toastNext": "Estimate recorded. Voting on the next story.",
  "mode.stories.placeholder": "e.g. User login flow, PROJ-42…",
  "mode.stories.hintText":
    "Add stories to track estimates by name, or just start voting without them. Both work.",
  "mode.stories.recordNext": "& Estimate Next Story",
  "mode.tasks.label": "Tasks",
  "mode.tasks.desc": "Estimate tasks within stories",
  "mode.tasks.singular": "task",
  "mode.tasks.plural": "tasks",
  "mode.tasks.queueTitle": "Task Queue",
  "mode.tasks.progressLabel": "Task",
  "mode.tasks.bannerLabel": "Estimating task",
  "mode.tasks.allDoneText": "tasks estimated",
  "mode.tasks.backlogLabel": "Task list",
  "mode.tasks.toastDone": "Task estimated. Vote on the next task.",
  "mode.tasks.toastNext": "Estimate recorded. Voting on the next task.",
  "mode.tasks.placeholder": "e.g. Build login API, Write unit tests, PROJ-42-1…",
  "mode.tasks.hintText":
    "Add tasks to track estimates by name, or just start voting without them. Both work.",
  "mode.tasks.recordNext": "& Estimate Next Task",

  /* ── Misc ── */
  "app.connecting": "Connecting…",
  "app.roomTitle": "Planning Poker Room | Point Poker",
  "app.roomDesc":
    "Live Point Poker estimation room for sprint planning. Room URLs are for active sessions and are not indexed.",
  "app.teamRoomTitle": "Team Room | Point Poker",
  "app.teamRoomDesc":
    "Join a Point Poker Team Room to estimate stories live with your team. Team Room URLs are for active sessions and are not indexed.",
  "app.dashboardTitle": "Usage dashboard | Point Poker",
  "app.dashboardDesc": "Owner-only usage analytics.",
};
